import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LayoutDashboard, FileText, Calendar, BarChart3, Mic, Settings as SettingsIcon } from 'lucide-react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import CookieBanner from './components/CookieBanner';
import TrackingEnforcer from './components/TrackingEnforcer';
import { PwaInstallBanner } from './components/PwaInstall';
import LandingPage from './components/LandingPage';
import AuthPage from './components/AuthPage';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import ArticleList from './components/ArticleList';
import VoiceAI from './components/VoiceAI';
import AIAssistant from './components/AIAssistant';
import Credits from './components/Credits';
import AdminDashboard from './components/AdminDashboard';
import Settings from './components/Settings';
import ScheduledPosts from './components/ScheduledPosts';
import Analytics from './components/Analytics';
import Integrations from './components/Integrations';
import BrandVoice from './components/BrandVoice';
import ContentTools from './components/ContentTools';
import AppSumoPage from './components/AppSumoPage';
import ConfirmEmail from './components/ConfirmEmail';
import PublicLayout from './components/PublicLayout';
import FeaturesPage from './pages/FeaturesPage';
import PricingPage from './pages/PricingPage';
import HowItWorksPage from './pages/HowItWorksPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import RoadmapPage from './pages/RoadmapPage';
import StatusPage from './pages/StatusPage';
import { PrivacyPage, TermsPage, CookiePage, DpaPage, ImpressumPage } from './pages/LegalPages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Payment Success Handler
const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { getAuthHeaders, refreshSubscription } = useAuth();
  const [status, setStatus] = useState('processing');
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId]);

  const pollPaymentStatus = async (attempts = 0) => {
    if (attempts >= 5) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/payments/status/${sessionId}`, {
        headers: getAuthHeaders()
      });

      if (response.data.payment_status === 'paid') {
        setStatus('success');
        await refreshSubscription();
        setTimeout(() => navigate('/'), 2000);
      } else if (response.data.status === 'expired') {
        setStatus('expired');
      } else {
        setTimeout(() => pollPaymentStatus(attempts + 1), 2000);
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
      <div className="card p-10 text-center max-w-md">
        {status === 'processing' && (
          <>
            <div className="spinner mx-auto mb-4"></div>
            <h2 className="font-serif text-2xl mb-2">Processing payment...</h2>
            <p className="text-sm text-[var(--color-muted)]">Please wait while we confirm your purchase.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 bg-green-50">
              <svg className="w-7 h-7 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="font-serif text-2xl mb-2">Payment successful!</h2>
            <p className="text-sm text-[var(--color-muted)]">Your credits have been added. Redirecting...</p>
          </>
        )}
        {status === 'error' && (
          <>
            <h2 className="font-serif text-2xl mb-2">Something went wrong</h2>
            <p className="text-sm text-[var(--color-muted)] mb-6">Please contact support if you were charged.</p>
            <button onClick={() => navigate('/')} className="btn btn-primary">
              Return to dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// Payment Cancel Handler
const PaymentCancel = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
      <div className="card p-10 text-center max-w-md">
        <h2 className="font-serif text-2xl mb-2">Payment cancelled</h2>
        <p className="text-sm text-[var(--color-muted)] mb-6">No charges were made.</p>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          Return to dashboard
        </button>
      </div>
    </div>
  );
};

// Main App Content
const AppContent = () => {
  const { user, loading, isAuthenticated } = useAuth();
  const [showAuth, setShowAuth] = useState(false);
  const [activeView, setActiveView] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Founder/admin status is decided by the backend (UserResponse.is_founder,
  // sourced from FOUNDER_EMAILS in models.py) — the frontend never hardcodes it.
  const isFounder = !!user?.is_founder;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)]">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (showAuth) {
      return <AuthPage onBack={() => setShowAuth(false)} />;
    }
    return <LandingPage onGetStarted={() => setShowAuth(true)} />;
  }

  const renderView = () => {
    switch (activeView) {
      case 'dashboard':
        return <Dashboard onNavigate={setActiveView} />;
      case 'articles':
        return <ArticleList onNavigate={setActiveView} />;
      case 'scheduled':
        return <ScheduledPosts />;
      case 'analytics':
        return <Analytics />;
      case 'voice':
        return <VoiceAI />;
      case 'assistant':
        return <AIAssistant />;
      case 'credits':
        return <Credits />;
      case 'integrations':
        return <Integrations />;
      case 'brand':
        return <BrandVoice onNavigate={setActiveView} />;
      case 'tools':
        return <ContentTools />;
      case 'settings':
        return <Settings />;
      case 'admin':
        return isFounder ? <AdminDashboard /> : <ComingSoon title="Admin (No Access)" />;
      default:
        return <Dashboard onNavigate={setActiveView} />;
    }
  };

  return (
    <div className="flex min-h-screen bg-[var(--color-bg)]">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      <Sidebar
        activeView={activeView}
        onViewChange={(v) => { setActiveView(v); setSidebarOpen(false); }}
        isFounder={isFounder}
        mobileOpen={sidebarOpen}
      />
      <main className="main-content">
        {/* Mobile Header */}
        <div className="flex items-center justify-between mb-6 md:hidden">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 rounded-xl bg-[var(--color-surface)]"
            data-testid="mobile-menu-btn"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white font-bold text-xs"
                 style={{background: 'linear-gradient(135deg, #E8735C, #C9A962)'}}>S</div>
            <span className="font-display font-semibold text-sm">Stilos</span>
          </div>
          <div style={{width: 34}} />
        </div>
        {renderView()}
      </main>
      <MobileBottomNav activeView={activeView} onViewChange={setActiveView} />
    </div>
  );
};

// Coming Soon Placeholder
const ComingSoon = ({ title }) => (
  <div className="fade-in">
    <h1 className="font-serif text-3xl mb-2">{title}</h1>
    <p className="text-[var(--color-muted)] mb-8">This feature is coming soon.</p>
    <div className="card p-12 text-center">
      <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center mb-4 bg-[var(--color-surface)]">
        <svg className="w-7 h-7 text-[var(--color-coral)]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h3 className="font-display text-lg mb-2">Coming Soon</h3>
      <p className="text-sm text-[var(--color-muted)]">We're working on {title.toLowerCase()}. Check back soon!</p>
    </div>
  </div>
);

// Mobile Bottom Navigation
const MobileBottomNav = ({ activeView, onViewChange }) => {
  const tabs = [
    { id: 'dashboard', icon: LayoutDashboard, label: 'Home' },
    { id: 'articles', icon: FileText, label: 'Articles' },
    { id: 'scheduled', icon: Calendar, label: 'Schedule' },
    { id: 'analytics', icon: BarChart3, label: 'Analytics' },
    { id: 'settings', icon: SettingsIcon, label: 'More' },
  ];
  return (
    <nav className="mobile-bottom-nav" data-testid="mobile-bottom-nav">
      {tabs.map(({ id, icon: Icon, label }) => (
        <button
          key={id}
          onClick={() => onViewChange(id)}
          className={`mobile-nav-item ${activeView === id ? 'active' : ''}`}
          data-testid={`mobile-nav-${id}`}
        >
          <Icon className="w-5 h-5" />
          <span>{label}</span>
        </button>
      ))}
    </nav>
  );
};

// App Router
function App() {
  const [showAuth, setShowAuth] = useState(false);
  const handleGetStarted = () => setShowAuth(true);

  return (
    <CookieConsentProvider>
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Payment routes */}
          <Route path="/payment/success" element={<PaymentSuccess />} />
          <Route path="/payment/cancel" element={<PaymentCancel />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/appsumo" element={<AppSumoPage />} />
          
          {/* Public pages with shared layout */}
          <Route path="/features" element={<PublicLayout onGetStarted={handleGetStarted}>{showAuth ? <AuthPage onBack={() => setShowAuth(false)} /> : <FeaturesPage onGetStarted={handleGetStarted} />}</PublicLayout>} />
          <Route path="/pricing" element={<PublicLayout onGetStarted={handleGetStarted}>{showAuth ? <AuthPage onBack={() => setShowAuth(false)} /> : <PricingPage onGetStarted={handleGetStarted} />}</PublicLayout>} />
          <Route path="/how-it-works" element={<PublicLayout onGetStarted={handleGetStarted}>{showAuth ? <AuthPage onBack={() => setShowAuth(false)} /> : <HowItWorksPage onGetStarted={handleGetStarted} />}</PublicLayout>} />
          <Route path="/about" element={<PublicLayout onGetStarted={handleGetStarted}>{showAuth ? <AuthPage onBack={() => setShowAuth(false)} /> : <AboutPage onGetStarted={handleGetStarted} />}</PublicLayout>} />
          <Route path="/contact" element={<PublicLayout onGetStarted={handleGetStarted}><ContactPage /></PublicLayout>} />
          <Route path="/knowledge-base" element={<PublicLayout onGetStarted={handleGetStarted}><KnowledgeBasePage /></PublicLayout>} />
          <Route path="/roadmap" element={<PublicLayout onGetStarted={handleGetStarted}><RoadmapPage /></PublicLayout>} />
          <Route path="/system-status" element={<PublicLayout onGetStarted={handleGetStarted}><StatusPage /></PublicLayout>} />
          
          {/* Legal pages */}
          <Route path="/privacy" element={<PublicLayout onGetStarted={handleGetStarted}><PrivacyPage /></PublicLayout>} />
          <Route path="/terms" element={<PublicLayout onGetStarted={handleGetStarted}><TermsPage /></PublicLayout>} />
          <Route path="/cookies" element={<PublicLayout onGetStarted={handleGetStarted}><CookiePage /></PublicLayout>} />
          <Route path="/dpa" element={<PublicLayout onGetStarted={handleGetStarted}><DpaPage /></PublicLayout>} />
          <Route path="/impressum" element={<PublicLayout onGetStarted={handleGetStarted}><ImpressumPage /></PublicLayout>} />

          {/* Main app */}
          <Route path="/*" element={<AppContent />} />
        </Routes>
        <CookieBanner />
        <TrackingEnforcer />
        <PwaInstallBanner />
      </BrowserRouter>
    </AuthProvider>
    </CookieConsentProvider>
  );
}

export default App;
